"use client";

import { useEffect, useState } from "react";
import {
  getSessionStats,
  _debugForceTimeout,
  _debugForceMidnightCrossing,
  toggleDebugTimeout,
  isUsingDebugTimeout,
  endSessionOnPageHide,
} from "@/analytics/session";
import { useGate } from "statsig-react";

export default function SessionDebug() {
  const [stats, setStats] = useState<any>(null);
  const [showControls, setShowControls] = useState(false);
  const [isDebugMode, setIsDebugMode] = useState(isUsingDebugTimeout());

  // Add yourself (userId)to statsig gate to see session debug panel
  // https://console.statsig.com/62vdU8hvJIRJ48q87IHdo6/gates/session_timer_debug_panel
  const sessionDebugGate = useGate("session_timer_debug_panel");
  const isDevelopment = process.env.NODE_ENV === "development";
  const isSessionDebugEnabled = sessionDebugGate.value && isDevelopment;

  useEffect(() => {
    if (!isSessionDebugEnabled) return;

    const interval = setInterval(() => {
      const currentStats = getSessionStats();
      setStats(currentStats);
      setIsDebugMode(isUsingDebugTimeout());
    }, 1000);

    return () => clearInterval(interval);
  }, [isSessionDebugEnabled]);

  const handleToggleTimeout = () => {
    const newMode = toggleDebugTimeout();
    setIsDebugMode(newMode);
  };

  const handleForcePagehide = () => {
    endSessionOnPageHide();
  };

  if (!isSessionDebugEnabled) return null;

  if (!stats) return null;

  return (
    <div className="fixed bottom-4 right-4 bg-black/80 text-white p-4 rounded-lg text-sm z-50 max-w-md overflow-auto max-h-[80vh]">
      <div className="flex justify-between items-center mb-2">
        <h3 className="font-bold">Session Debug</h3>
        <button
          onClick={() => setShowControls(!showControls)}
          className="text-xs bg-gray-700 hover:bg-gray-600 px-2 py-1 rounded"
        >
          {showControls ? "Hide Controls" : "Show Controls"}
        </button>
      </div>

      <div
        className={
          isDebugMode
            ? "bg-yellow-800/50 p-2 rounded mb-2"
            : "bg-green-800/50 p-2 rounded mb-2"
        }
      >
        <span
          className={
            isDebugMode
              ? "text-yellow-300 font-semibold"
              : "text-green-300 font-semibold"
          }
        >
          {isDebugMode
            ? "🐞 DEBUG MODE: 15s timeout"
            : "✓ PRODUCTION MODE: 30m timeout"}
        </span>
      </div>

      <pre className="text-xs bg-gray-900/50 p-2 rounded overflow-auto">
        {JSON.stringify(stats, null, 2)}
      </pre>

      {showControls && (
        <div className="mt-4 pt-4 border-t border-gray-700">
          <p className="mb-2 text-xs font-bold">Test Controls:</p>
          <div className="flex flex-col gap-2">
            <button
              onClick={handleToggleTimeout}
              className={
                isDebugMode
                  ? "bg-green-600 hover:bg-green-500 text-xs px-2 py-1 rounded flex items-center justify-between"
                  : "bg-yellow-600 hover:bg-yellow-500 text-xs px-2 py-1 rounded flex items-center justify-between"
              }
            >
              <span>
                {isDebugMode
                  ? "→ Switch to 30min timeout (Production)"
                  : "→ Switch to 15sec timeout (Debug)"}
              </span>
            </button>

            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                onClick={_debugForceTimeout}
                className="bg-red-600 hover:bg-red-500 text-xs px-2 py-1 rounded"
              >
                Force Timeout
              </button>

              <button
                onClick={_debugForceMidnightCrossing}
                className="bg-purple-600 hover:bg-purple-500 text-xs px-2 py-1 rounded"
              >
                Force Midnight Reset
              </button>
            </div>

            <button
              onClick={handleForcePagehide}
              className="bg-blue-600 hover:bg-blue-500 text-xs px-2 py-1 rounded mt-2"
            >
              Simulate Pagehide
            </button>

            <div className="bg-gray-800/50 p-2 rounded text-xs mt-2">
              <p className="font-bold mb-1">Testing Session Timeout:</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>Start a session by interacting with the page</li>
                <li>Stop all interaction and wait for timeout</li>
                <li>Verify session_count increments after timeout</li>
                <li>Check timeout_in counts down correctly</li>
                <li>Monitor Mixpanel to confirm events are received</li>
              </ol>

              <p className="font-bold mt-3">Testing pagehide:</p>
              <ol className="list-decimal pl-4 space-y-1">
                <li>
                  Click Simulate Pagehide and see if the session ends with
                  end_reason=pagehide.
                </li>
                <li>
                  Check Mixpanel or Statsig logs for session_ended event with
                  reason=pagehide.
                </li>
                <li>
                  Physically close or refresh the tab, confirm real pagehide
                  triggers a final session_ended.
                </li>
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
